"use client";

import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowRightIcon } from "lucide-react";
import { useMemo, useState } from "react";
import { useGradingTable } from "./useGrade";

interface ValidationResult<T> {
  value: T | null;
  error: string | null;
}

export default function ScoreLookup() {
  const { possibleScores, maxScore, passingGrade } = useGradingTable();
  const [scoreInput, setScoreInput] = useState("");
  const [gradeInput, setGradeInput] = useState("");

  // Score -> Grade lookup with validation
  const scoreValidation = useMemo((): ValidationResult<number> => {
    if (scoreInput === "") {
      return { value: null, error: null };
    }

    const score = parseFloat(scoreInput);

    if (isNaN(score)) {
      return { value: null, error: "Voer een geldig nummer in" };
    }

    if (score < 0) {
      return { value: null, error: "Score mag niet negatief zijn" };
    }

    if (score > maxScore) {
      return {
        value: null,
        error: `Score mag niet hoger zijn dan ${maxScore}`,
      };
    }

    const match = possibleScores.find((s) => s.score === score);
    if (match) {
      return { value: match.grade, error: null };
    }

    const closest = possibleScores.reduce((prev, curr) =>
      Math.abs(curr.score - score) < Math.abs(prev.score - score) ? curr : prev,
    );

    return { value: closest?.grade ?? null, error: null };
  }, [scoreInput, possibleScores, maxScore]);

  // Grade -> Score lookup with validation
  const gradeValidation = useMemo((): ValidationResult<number> => {
    if (gradeInput === "") {
      return { value: null, error: null };
    }

    const targetGrade = parseFloat(gradeInput);

    if (isNaN(targetGrade)) {
      return { value: null, error: "Voer een geldig nummer in" };
    }

    if (targetGrade < 1) {
      return { value: null, error: "Cijfer moet minimaal 1.0 zijn" };
    }

    if (targetGrade > 10) {
      return { value: null, error: "Cijfer mag maximaal 10.0 zijn" };
    }

    const matching = possibleScores.filter((s) => s.grade >= targetGrade);
    if (matching.length === 0) {
      return {
        value: null,
        error: "Dit cijfer is niet haalbaar met de huidige instellingen",
      };
    }

    const minScore = matching.reduce((prev, curr) =>
      curr.score < prev.score ? curr : prev,
    );

    return { value: minScore.score, error: null };
  }, [gradeInput, possibleScores]);

  const gradeForScore = scoreValidation.value;
  const scoreForGrade = gradeValidation.value;

  const isPassing =
    gradeForScore !== null && gradeForScore >= Number(passingGrade);
  const isTargetPassing =
    scoreForGrade !== null &&
    (possibleScores.find((s) => s.score === scoreForGrade)?.grade ?? 0) >=
      Number(passingGrade);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 order-3 items-start">
      {/* Score -> Grade Calculator */}
      <Card className="overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-200 pt-0 pb-0">
        <CardContent className="p-0">
          <div className="bg-muted/40 px-6 md:px-8 py-5 border-b">
            <div className="flex items-center gap-2.5 text-base font-medium text-foreground">
              <span>Score</span>
              <ArrowRightIcon className="h-4 w-4 text-muted-foreground/70" />
              <span>Cijfer</span>
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              Welk cijfer krijg ik met deze score?
            </p>
          </div>
          <div className="px-6 md:px-8 py-8 md:py-6">
            <div className="min-h-32 flex items-center gap-8 md:gap-10">
              <div className="flex-1 space-y-3">
                <Label htmlFor="score-lookup" className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                  Jouw score
                </Label>
                <Input
                  id="score-lookup"
                  type="number"
                  min={0}
                  max={maxScore}
                  step="any"
                  placeholder={`0 - ${maxScore}`}
                  value={scoreInput}
                  onChange={(e) => setScoreInput(e.target.value)}
                  className={`text-xl md:text-2xl h-14 md:h-16 font-medium ${scoreValidation.error ? "border-warning focus-visible:ring-warning/30" : ""}`}
                />
                {scoreValidation.error && (
                  <p className="text-sm text-warning">
                    {scoreValidation.error}
                  </p>
                )}
              </div>

              <div className="flex-1 flex flex-col items-center justify-center pt-6">
                <div
                  className={`text-5xl md:text-6xl font-bold tabular-nums tracking-tight ${
                    gradeForScore !== null
                      ? isPassing
                        ? "text-success"
                        : "text-warning"
                      : "text-muted-foreground/20"
                  }`}
                >
                  {gradeForScore !== null ? gradeForScore.toFixed(1) : "-.-"}
                </div>
                {gradeForScore !== null && (
                  <Badge
                    variant={isPassing ? "success" : "warning"}
                    className="mt-3 text-sm px-3 py-1"
                  >
                    {isPassing ? "Voldoende" : "Onvoldoende"}
                  </Badge>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Grade -> Score Calculator (Reverse) */}
      <Card className="overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-200 pt-0 pb-0">
        <CardContent className="p-0">
          <div className="bg-muted/40 px-6 md:px-8 py-5 border-b">
            <div className="flex items-center gap-2.5 text-base font-medium text-foreground">
              <span>Cijfer</span>
              <ArrowRightIcon className="h-4 w-4 text-muted-foreground/70" />
              <span>Score</span>
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              Welke score heb ik nodig voor dit cijfer?
            </p>
          </div>
          <div className="px-6 md:px-8 py-8 md:py-6">
            <div className="min-h-32 flex items-center gap-8 md:gap-10">
              <div className="flex-1 space-y-3">
                <Label htmlFor="grade-lookup" className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                  Gewenst cijfer
                </Label>
                <Input
                  id="grade-lookup"
                  type="number"
                  min={1}
                  max={10}
                  step="0.1"
                  placeholder="1.0 - 10.0"
                  value={gradeInput}
                  onChange={(e) => setGradeInput(e.target.value)}
                  className={`text-xl md:text-2xl h-14 md:h-16 font-medium ${gradeValidation.error ? "border-warning focus-visible:ring-warning/30" : ""}`}
                />
                {gradeValidation.error && (
                  <p className="text-sm text-warning">
                    {gradeValidation.error}
                  </p>
                )}
              </div>

              <div className="flex-1 flex flex-col items-center justify-center pt-6">
                <div
                  className={`text-5xl md:text-6xl font-bold tabular-nums tracking-tight ${
                    scoreForGrade !== null
                      ? isTargetPassing
                        ? "text-success"
                        : "text-warning"
                      : "text-muted-foreground/20"
                  }`}
                >
                  {scoreForGrade !== null ? scoreForGrade : "--"}
                </div>
                {scoreForGrade !== null && (
                  <Badge variant="outline" className="mt-3 text-sm px-3 py-1 text-muted-foreground">
                    van {maxScore} punten
                  </Badge>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
